import * as React from 'react';

import { DataGridColumn } from '@rematter/ui-web';
import { cn } from '@rematter/ui-web/lib';

import { useTranslations } from '../../../i18n/useTranslations';
import { InboundTollingAgreementRecord } from '../../../types/inboundTollingAgreement';

import { getLocale } from '~/shared/utils/locale';

type UseInboundTollingColumnsArgs = {
  onRowClick?: (cell: { row: InboundTollingAgreementRecord }) => void;
};

const formatCurrency = (amount: number, currencyCode: string): string =>
  new Intl.NumberFormat(getLocale(), {
    style: 'currency',
    currency: currencyCode,
  }).format(amount);

export const useInboundTollingColumns = ({
  onRowClick,
}: UseInboundTollingColumnsArgs = {}): DataGridColumn<InboundTollingAgreementRecord>[] => {
  const t = useTranslations();

  return React.useMemo(() => {
    const columns: DataGridColumn<InboundTollingAgreementRecord>[] = [
      {
        key: 'agreementNumber',
        header: t.tollProcessingAgreementsView.table.columns.agreementNumber,
        onClick: onRowClick,
        cell: ({ row }) => (
          <span className="font-medium">{row.agreementNumber}</span>
        ),
        width: 150,
        sortable: true,
      },
      {
        key: 'customer',
        header: t.tollProcessingAgreementsView.table.columns.customer,
        onClick: onRowClick,
        cell: ({ row }) => <span>{row.customerAccountName || '—'}</span>,
        width: 200,
      },
      {
        key: 'processingType',
        header: t.tollProcessingAgreementsView.table.columns.processingType,
        onClick: onRowClick,
        cell: ({ row }) => <span>{row.processingTypeName || '—'}</span>,
        width: 150,
      },
      {
        key: 'effectiveDate',
        header: t.tollProcessingAgreementsView.table.columns.effectiveDate,
        onClick: onRowClick,
        cell: ({ row }) => (
          <span>{new Date(row.effectiveDate).toLocaleDateString()}</span>
        ),
        width: 120,
        sortable: true,
      },
      {
        key: 'expirationDate',
        header: t.tollProcessingAgreementsView.table.columns.expirationDate,
        onClick: onRowClick,
        cell: ({ row }) => (
          <span>
            {row.expirationDate
              ? new Date(row.expirationDate).toLocaleDateString()
              : '—'}
          </span>
        ),
        width: 120,
        sortable: true,
      },
      {
        key: 'pricePerUnit',
        header: t.tollProcessingAgreementsView.table.columns.pricePerUnit,
        onClick: onRowClick,
        cell: ({ row }) => (
          <span>
            {row.pricePerUnitAmount && row.pricePerUnitCurrency
              ? `${formatCurrency(row.pricePerUnitAmount, row.pricePerUnitCurrency)}${row.priceUnit ? `/${row.priceUnit}` : ''}`
              : '—'}
          </span>
        ),
        width: 120,
      },
      {
        key: 'isActive',
        header: t.tollProcessingAgreementsView.table.columns.isActive,
        onClick: onRowClick,
        cell: ({ row }) => (
          <span
            className={cn(
              'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
              row.isActive
                ? 'bg-success/10 text-success'
                : 'bg-neutral/10 text-neutral/60',
            )}
          >
            {row.isActive ? 'Active' : 'Inactive'}
          </span>
        ),
        width: 100,
      },
    ];

    return columns;
  }, [t, onRowClick]);
};
